// Reads the source waveform the backend (aivoice-video) already generated: a
// dense peaks file of one byte per peak, 0..255. This is a cheap fetch + scale —
// no whole-video download and no Web Audio decode, unlike `audioWaveform.js`.

/**
 * Fetch the peaks file at `url` and return it EXACTLY as the backend produced it:
 * one point per byte, each `byte / 255` (absolute 0..1). No re-normalization and
 * no downsampling — the shape on screen is byte-for-byte the BE file. Resolves
 * null on any error (network, empty) so the caller can fall back.
 *
 * Note: a long source yields many points (≈ peaksPerSecond × duration) — far
 * more than a lane can show. The media track draws them through
 * <VirtualizedWaveform>, which reduces the visible window to the pixels it
 * covers; the full-resolution array here stays the single source of truth.
 *
 * @returns {Promise<Float32Array | null>}
 */
export const fetchSourcePeaks = async (url) => {
  if (!url) return null;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const bytes = new Uint8Array(await res.arrayBuffer());
    if (!bytes.length) return null;
    // Typed, not number[]: a 90-minute source is ~270k points — boxed doubles
    // would cost several MB and GC pressure for the same values.
    const peaks = new Float32Array(bytes.length);
    for (let i = 0; i < bytes.length; i += 1) peaks[i] = bytes[i] / 255;
    return peaks;
  } catch (error) {
    return null;
  }
};
